import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { isLocalLoginEnabled, isOidcEnabled, startOidcLogin } from '../utils/oidcAuth';
import { isSuperAdmin } from '../utils/auth';
import { useLocalization } from '../contexts/LocalizationContext';
import salesOpsLogo from '../assets/salesops-logo.png';
import sphereRight from '../assets/login-sphere-right.png';
import './Login.css';

const BRAND_NAME = 'Fronta';
const BRAND_TAGLINE = 'SALES OPERATIONS WORKSTATION';
const REMEMBER_USER_KEY = 'loginUser';
const REMEMBER_FLAG_KEY = 'loginRemember';

export function Login() {
  const { t } = useLocalization();
  const [email, setEmail] = useState(() => localStorage.getItem(REMEMBER_USER_KEY) || '');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(() => localStorage.getItem(REMEMBER_FLAG_KEY) !== '0');
  const [msg, setMsg] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [ssoLoading, setSsoLoading] = useState(false);
  // G-L-07 — 2FA ikinci adımı. Dolu ise parola doğrulanmış ama oturum HENÜZ AÇILMAMIŞTIR:
  // sunucu bilerek token vermez, yalnız 5 dakikalık tek kullanımlık bir meydan okuma döner.
  const [twoFactorToken, setTwoFactorToken] = useState(null);
  const [otp, setOtp] = useState('');
  // G-L-08 — captcha yalnız eşik aşıldığında istenir; normal kullanıcı hiç görmez.
  const [captcha, setCaptcha] = useState(null);   // { ticket, svg }
  const [captchaAnswer, setCaptchaAnswer] = useState('');
  const navigate = useNavigate();
  const localLoginEnabled = isLocalLoginEnabled();
  const oidcEnabled = isOidcEnabled();

  useEffect(() => {
    localStorage.setItem(REMEMBER_FLAG_KEY, rememberMe ? '1' : '0');
    if (!rememberMe) localStorage.removeItem(REMEMBER_USER_KEY);
  }, [rememberMe]);

  // #259 Oturum zaman aşımı ile buraya düşüldüyse nedeni bir kez göster.
  // Sebep kodları utils/sessionLog SESSION_END_REASONS ile aynıdır; 'manual' bilerek
  // listede yoktur (kullanıcı kendi çıktıysa mesaj göstermeye gerek yok).
  useEffect(() => {
    let reason;
    try { reason = sessionStorage.getItem('sessionEndReason'); sessionStorage.removeItem('sessionEndReason'); } catch { /* yoksay */ }
    if (!reason || reason === 'manual' || reason === 'oidc-logout') return;

    const messages = {
      idle: t('auth.session.idle', 'Hareketsizlik nedeniyle oturumunuz güvenlik gereği sonlandırıldı. Lütfen yeniden giriş yapın.'),
      expired: t('auth.session.expired', 'Oturum süreniz doldu. Lütfen yeniden giriş yapın.'),
      // Sunucu 401'i: client tarafında token hâlâ geçerli görünüyordu — kullanıcının
      // gördüğü metin aynı olsa da günlükte ayrı sebep olarak durur.
      'api-401': t('auth.session.unauthorized', 'Oturumunuz sunucu tarafından doğrulanamadı. Lütfen yeniden giriş yapın.'),
      // Başka bir sekme oturumu kapattı — token tarayıcı genelinde paylaşıldığı için
      // bu sekme de düştü. Bu mesaj olmadan çıkış tamamen sebepsiz görünüyordu.
      'cross-tab': t('auth.session.crossTab', 'Oturumunuz başka bir sekmede sonlandırıldığı için burada da kapatıldı. Lütfen yeniden giriş yapın.'),
    };
    // Tanınmayan sebep de sessiz kalmasın: kodu göster, destek günlükten eşleştirsin.
    setError(messages[reason] ?? t('auth.session.unknown', 'Oturumunuz sonlandırıldı. Lütfen yeniden giriş yapın.') + ` (${reason})`);
  }, [t]);

  /** G-L-08 — Yeni captcha çeker. Her denemede yenilenmeli: bilet tek kullanımlıktır. */
  const refreshCaptcha = async () => {
    try {
      const res = await fetch('/api/auth/captcha');
      if (!res.ok) return;
      const body = await res.json();
      setCaptcha(body?.data ?? body);
      setCaptchaAnswer('');
    } catch { /* captcha alınamazsa giriş yine denenir; sunucu gerekiyorsa 428 döner */ }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setMsg('');
    try {
      const response = await fetch('/api/auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email,
          password,
          platform: 1,
          captchaTicket: captcha?.ticket,
          captchaAnswer: captchaAnswer || undefined,
        }),
      });

      // 428 = captcha gerekli. Sunucu bunu parola DENENMEDEN önce döner.
      if (response.status === 428) {
        const body = await response.json().catch(() => null);
        setError(body?.message || body?.Message || t('auth.captcha.required', 'Güvenlik doğrulaması gerekli.'));
        await refreshCaptcha();
        return;
      }
      if (!response.ok) {
        const body = await response.json().catch(() => null);
        setError(body?.message || body?.Message || t('auth.errors.loginFailed', 'Giriş başarısız.'));
        // Başarısız denemeden sonra captcha eşiği aşılmış olabilir — bileti yenile ki
        // kullanıcı bir sonraki denemede tükenmiş bir bileti göndermesin.
        if (captcha) await refreshCaptcha();
        return;
      }
      const result = await response.json();
      // /api/auth/login yanıtı ApiResponse zarfı KULLANMAZ, doğrudan AuthLoginResponse döner.
      const data = result.data ?? result;

      // G-L-07: 2FA açıksa sunucu BİLEREK token vermez, yalnız meydan okuma döner.
      if (data.requiresTwoFactor && data.twoFactorToken) {
        setTwoFactorToken(data.twoFactorToken);
        // Kanal mesajı ayrı: "uygulamanızdaki kod" ile "e-postanıza gönderilen kod" farklı
        // şeylerdir; tek bir genel mesaj kullanıcıyı yanlış yere baktırır.
        setMsg(
          data.twoFactorChannel === 'EMAIL'
            ? t('auth.2fa.promptEmail', 'E-posta adresinize gönderilen 6 haneli kodu girin.')
            : data.twoFactorChannel === 'SMS'
              ? t('auth.2fa.promptSms', 'Telefonunuza gönderilen 6 haneli kodu girin.')
              : t('auth.2fa.prompt', 'Doğrulama uygulamanızdaki 6 haneli kodu girin.'),
        );
        return;
      }

      if (rememberMe) localStorage.setItem(REMEMBER_USER_KEY, email);
      // Uygulamanın geri kalanı 'token' anahtarını okur; yeni uç accessToken adıyla döner.
      localStorage.setItem('token', data.accessToken);
      if (data.refreshToken) localStorage.setItem('refreshToken', data.refreshToken);
      localStorage.setItem('user', JSON.stringify({ ...data, token: data.accessToken, isLoginOk: true }));
      window.dispatchEvent(new Event('auth:login'));

      // İlk giriş veya şifre süresi doldu: kullanıcı önce şifresini değiştirmeli. Geçici şifreyi
      // (mevcut şifre olarak) değişim ekranına taşı ki kullanıcı tekrar yazmasın.
      if (data.mustChangePassword) {
        setMsg(t('auth.mustChange', 'İlk giriş: şifrenizi belirlemeniz gerekiyor...'));
        navigate('/change-password', { state: { currentPassword: password } });
        return;
      }
      setMsg(t('auth.success', 'Giriş başarılı, yönlendiriliyor...'));
      // SuperAdmin'in dashboard'ı yok; varsayılan ekran tenant listesi olsun.
      navigate(isSuperAdmin() ? '/tenants' : '/dashboards/general');
    } catch {
      setError(t('auth.errors.connection', 'Bağlantı hatası. Lütfen tekrar deneyin.'));
    } finally {
      setLoading(false);
    }
  };

  /**
   * G-L-07 — Girişin ikinci adımı. Meydan okuma + OTP kodu birlikte gönderilir; doğruysa
   * gerçek oturum BURADA açılır. Yanlış kod da başarısız giriş sayılır (kilitleme sayacı işler),
   * bu yüzden sınırsız deneme yapılamaz.
   */
  const handleOtpSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const response = await fetch('/api/auth/2fa/login-verify', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ twoFactorToken, code: otp }),
      });
      const result = await response.json().catch(() => null);
      if (!response.ok) {
        setError(result?.message || result?.Message || t('auth.2fa.invalid', 'Doğrulama kodu hatalı.'));
        // Meydan okuma TEK KULLANIMLIKTIR: sunucuda tüketildi. Kullanıcıyı parola adımına
        // geri döndürmezsek boş yere doğru kodu girmeye çalışır ve her seferinde hata alır.
        setTwoFactorToken(null);
        setOtp('');
        setMsg('');
        return;
      }

      const data = result?.data ?? result;
      if (rememberMe) localStorage.setItem(REMEMBER_USER_KEY, email);
      // Yeni uç accessToken/refreshToken döner; uygulamanın geri kalanı 'token' anahtarını okur.
      localStorage.setItem('token', data.accessToken);
      if (data.refreshToken) localStorage.setItem('refreshToken', data.refreshToken);
      localStorage.setItem('user', JSON.stringify({ ...data, token: data.accessToken, isLoginOk: true }));
      window.dispatchEvent(new Event('auth:login'));

      if (data.mustChangePassword) {
        setMsg(t('auth.mustChange', 'İlk giriş: şifrenizi belirlemeniz gerekiyor...'));
        navigate('/change-password', { state: { currentPassword: password } });
        return;
      }
      setMsg(t('auth.success', 'Giriş başarılı, yönlendiriliyor...'));
      navigate(isSuperAdmin() ? '/tenants' : '/dashboards/general');
    } catch {
      setError(t('auth.errors.connection', 'Bağlantı hatası. Lütfen tekrar deneyin.'));
    } finally {
      setLoading(false);
    }
  };

  const cancelTwoFactor = () => {
    setTwoFactorToken(null);
    setOtp('');
    setMsg('');
    setError('');
  };

  const handleSsoLogin = async () => {
    setSsoLoading(true);
    setError('');
    setMsg('');
    try {
      await startOidcLogin();
    } catch (err) {
      setError(err?.message || t('auth.errors.sso', 'SSO girişi başlatılamadı.'));
      setSsoLoading(false);
    }
  };

  const handleForgot = (e) => {
    e.preventDefault();
    navigate('/forgot-password');
  };

  return (
    <main className="login-screen" role="main" aria-label="Login">
      {/* Sol küre CSS ile çizilir (görsel değil) — bkz. Login.css .login-orb-left */}
      <div className="login-bg" aria-hidden="true">
        <div className="login-orb-left" />
      </div>

      <div className="login-stage">
        <div className="login-brand" aria-hidden="true">
          <img className="login-brand-logo" src={salesOpsLogo} alt="SalesOps" />
          <small className="login-brand-tag">{BRAND_TAGLINE}</small>
        </div>

        {/* Sağ küre kartla birlikte konumlanır → .login-cardbox içinde durmalı. */}
        <div className="login-cardbox">
          <img className="login-orb-right" src={sphereRight} alt="" aria-hidden="true" />
          <div className="login-card">
            <img className="login-logo" src={salesOpsLogo} alt="SalesOps" />

            <header className="login-card-head">
              <h1>{t('auth.login.title', 'Login')}</h1>
              <p>{t('auth.login.subtitle', `${BRAND_NAME} hesabınla giriş yap`)}</p>
            </header>

          {oidcEnabled && (
            <button
              type="button"
              className="login-sso"
              onClick={handleSsoLogin}
              disabled={ssoLoading}
            >
              {ssoLoading ? t('auth.ssoOpening', 'SSO açılıyor...') : t('auth.sso', 'SSO ile giriş yap')}
            </button>
          )}

          {oidcEnabled && localLoginEnabled && !twoFactorToken && (
            <div className="login-divider"><span>{t('auth.or', 'veya')}</span></div>
          )}

          {/* G-L-07 — ikinci adım. Parola doğrulandı ama oturum HENÜZ YOK; sunucu token vermedi.
              Kullanıcı adı/şifre alanları gizlenir ki kullanıcı hangi adımda olduğunu şaşırmasın. */}
          {twoFactorToken ? (
            <form onSubmit={handleOtpSubmit} autoComplete="off">
              <div className="login-field">
                <label htmlFor="login-otp">{t('auth.2fa.label', 'Doğrulama Kodu')}</label>
                <div className="login-input">
                  <input
                    id="login-otp"
                    type="text"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    maxLength={6}
                    value={otp}
                    onChange={(e) => setOtp(e.target.value.replace(/\D/g, ''))}
                    autoFocus
                    required
                  />
                </div>
              </div>
              <button type="submit" className="login-submit" disabled={loading || otp.length !== 6}>
                {loading ? t('auth.verifying', 'Doğrulanıyor…') : t('auth.2fa.verify', 'Doğrula ve Giriş Yap')}
              </button>
              <button type="button" className="login-link" onClick={cancelTwoFactor} disabled={loading}>
                {t('auth.2fa.back', 'Geri dön')}
              </button>
            </form>
          ) : localLoginEnabled && (
            <form onSubmit={handleSubmit} autoComplete="on">
              <div className="login-field">
                <label htmlFor="login-username">{t('auth.field.username', 'Kullanıcı Adı')}</label>
                <div className="login-input">
                  <input
                    id="login-username"
                    type="text"
                    name="username"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    autoComplete="username"
                    required
                  />
                </div>
              </div>

              <div className="login-field">
                <label htmlFor="login-password">{t('auth.field.password', 'Şifre')}</label>
                <div className="login-input">
                  <input
                    id="login-password"
                    type={showPassword ? 'text' : 'password'}
                    name="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    autoComplete="current-password"
                    required
                  />
                  <button
                    type="button"
                    className="login-eye"
                    onClick={() => setShowPassword((v) => !v)}
                    aria-label={showPassword ? t('auth.hidePassword', 'Şifreyi gizle') : t('auth.showPassword', 'Şifreyi göster')}
                    aria-pressed={showPassword}
                    tabIndex={-1}
                  >
                    {showPassword ? (
                      <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 10 8 10 8a18.5 18.5 0 0 1-2.16 3.19M6.61 6.61A18.5 18.5 0 0 0 2 12s3 8 10 8a9.12 9.12 0 0 0 5.39-1.61" />
                        <path d="M9.88 9.88a3 3 0 0 0 4.24 4.24" />
                        <line x1="2" y1="2" x2="22" y2="22" />
                      </svg>
                    ) : (
                      <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M2 12s3-8 10-8 10 8 10 8-3 8-10 8-10-8-10-8Z" />
                        <circle cx="12" cy="12" r="3" />
                      </svg>
                    )}
                  </button>
                </div>
              </div>

              <div className="login-row">
                <label className="login-check">
                  <input type="checkbox" checked={rememberMe} onChange={(e) => setRememberMe(e.target.checked)} />
                  <span>{t('auth.rememberMe', 'Beni Hatırla')}</span>
                </label>
                <a className="login-link" href="#" onClick={handleForgot}>
                  {t('auth.forgotPassword', 'Şifremi Unuttum')}
                </a>
              </div>

              {/* G-L-08 — captcha yalnız sunucu 428 döndükten sonra belirir; normal girişte
                  hiç görünmez ve kullanıcıyı yormaz. */}
              {captcha && (
                <div className="login-field">
                  <label htmlFor="login-captcha">{t('auth.captcha.label', 'Güvenlik Kodu')}</label>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
                    <span
                      style={{ lineHeight: 0, borderRadius: 8, overflow: 'hidden' }}
                      // SVG sunucuda üretilir ve kullanıcı girdisi içermez; XSS yüzeyi yok.
                      dangerouslySetInnerHTML={{ __html: captcha.svg }}
                    />
                    <button
                      type="button"
                      className="login-link"
                      onClick={refreshCaptcha}
                      title={t('auth.captcha.refresh', 'Yeni kod')}
                    >
                      ↻
                    </button>
                  </div>
                  <div className="login-input">
                    <input
                      id="login-captcha"
                      type="text"
                      autoComplete="off"
                      maxLength={5}
                      value={captchaAnswer}
                      onChange={(e) => setCaptchaAnswer(e.target.value)}
                      required
                    />
                  </div>
                </div>
              )}

              <button type="submit" className="login-submit" disabled={loading}>
                {loading ? t('auth.submitting', 'Giriş yapılıyor...') : t('auth.submit', 'Giriş Yap')}
              </button>

              <div className="login-foot">
                <div className="login-msg" aria-live="polite">{error ? error : msg}</div>
              </div>
            </form>
          )}

          {!localLoginEnabled && (
            <div className="login-foot">
              <div className="login-msg" aria-live="polite">{error || msg}</div>
            </div>
          )}
          </div>
        </div>
      </div>
    </main>
  );
}
